/**
 * Created by Ning Jiang on 2022-05-06.
 */

import {ReelStopPresentationController} from "../ReelStopPresentationController";
import {Serial} from "../../Serial";
import {IReelStateEventData} from "../../../core/reel/ReelState";
import {SerialSymbol} from "../../symbol/SerialSymbol";
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {Layers, SerialLayers} from "../../view/SerialLayers";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {gsap} from "gsap";
import {SerialSound} from "../../sound/SerialSound";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {SerialGameConfig} from "../../SerialGameConfig";

export class WinHighlightEffect extends PIXI.Container {

    public symHeight:number;


    constructor(symHeight:number) {
        super();
        this.symHeight = symHeight;
    }

    public playEffect():gsap.core.Timeline {
        const bg = new TimelineSprite(Serial.assetLoader.getTextures("individualWin/156px/156px_"));
        bg.blendMode = PIXI.BLEND_MODES.ADD;
        bg.anchor.set(0.5);
        bg.alpha = 0.3;
        bg.hide();
        this.addChild(bg);

        const frame = new PIXI.Sprite(Serial.assetLoader.getTextures(`individualWin/frameGreen${this.getSize()}px`)[0]);
        frame.blendMode = PIXI.BLEND_MODES.ADD;
        frame.anchor.set(0.5);
        frame.alpha = 0.3;
        frame.scale.set(0.95);
        this.addChild(frame);

        const heightMask = new PIXI.Graphics();
        heightMask.beginFill();
        heightMask.drawRect(-144 * 0.5, -this.symHeight * 0.5, 144, this.symHeight);
        heightMask.endFill();
        this.addChild(heightMask);
        bg.mask = heightMask;

        const tl = gsap.timeline();
        tl.add([
            <any>bg.getAnimationAutoShowHide(),
            gsap.to(bg, {alpha : 0.5, duration : 0.1}),
            gsap.to(frame, {alpha : 0.7, duration : 0.1}),
            gsap.to(frame.scale, {x : 1, y : 1, duration : 0.1})
        ]);
        tl.add([
            gsap.to(bg, {alpha : 0, duration : 0.4}),
            gsap.to(frame, {alpha : 0, duration : 0.4}),
            gsap.to(frame.scale, {x : 0.98, y : 0.98, duration : 0.4})
        ], 0.1);
        tl.add(() => {
            tl.pause();
            tl.kill();
            this.children.length = 0;
            bg.destroy();
            frame.destroy();
            heightMask.destroy();
        }, 0.5);

        return tl;
    }

    private getSize():string {
        if(this.symHeight > 150) {
            return `156`;
        }
        if(this.symHeight > 100) {
            return `104`;
        }
        return `78`;
    }
}

export class SymbolWinHighlightController extends ReelStopPresentationController {

    private _highlightSoundPlayedReel:number;
    private _winSymbols:string[] = [];
    private _layer:StageLayer;
    private _positionPlayed:boolean[][];
    private _currentStoppedPositions:boolean[][];
    private _init:boolean = false;

    constructor() {
        super("SymbolWinHighlightController");
        this._layer = SerialLayers.getLayer(Layers.WIN_PRESENTATIONS)!;

        if(Serial.model.isRestore && Serial.model.parsedInitData.nextMode.includes("RESPIN")) {
            Serial.model.parsedInitData.lockedSymbolsNextSpin.forEach(lockedReel => {
                lockedReel.forEach(lockedSymbol => {
                    if(lockedSymbol != null && (lockedSymbol.includes("L") || lockedSymbol.includes("M"))) {
                        if(this._winSymbols.indexOf(lockedSymbol) < 0) {
                            this._winSymbols.push(lockedSymbol);
                        }
                    }
                });
            });
        }
    }

    protected onReelBounce(data:IReelStateEventData):Promise<any> {
        this._currentStoppedPositions[data.reelId][2] = true;
        this._currentStoppedPositions[data.reelId][3] = true;
        this._currentStoppedPositions[data.reelId][4] = true;

        if(data.reelId < 2 && !Serial.model.parsedGameData.mode.includes("RESPIN")) {
            return Promise.resolve();
        }

        if(this.playHighlightSymbolsOnReel(data.reelId, true)) {
            for(let reelId = 0; reelId < data.reelId; reelId++) {
                this.playHighlightSymbolsOnReel(reelId, false);
                this.playHighlightOnEnhancerIfOpenLanded(reelId, 0, false);
                this.playHighlightOnEnhancerIfOpenLanded(reelId, 1, false);
                this.playHighlightOnEnhancerIfOpenLanded(reelId, 5, false);
                this.playHighlightOnEnhancerIfOpenLanded(reelId, 6, false);
            }
        }
        return Promise.resolve();
    }

    protected async onEnhancerReelBounce(data:IReelStateEventData):Promise<any> {
        this._currentStoppedPositions[data.reelId][data.positionId] = true;
        if(data.reelId < 2 && !Serial.model.parsedGameData.mode.includes("RESPIN")) {
            return Promise.resolve();
        }

        if(this.playHighlightOnEnhancerIfOpenLanded(data.reelId, data.positionId, true)) {
            for(let reelId = 0; reelId < data.reelId; reelId++) {
                this.playHighlightSymbolsOnReel(reelId, false);
                this.playHighlightOnEnhancerIfOpenLanded(reelId, 0, false);
                this.playHighlightOnEnhancerIfOpenLanded(reelId, 1, false);
                this.playHighlightOnEnhancerIfOpenLanded(reelId, 5, false);
                this.playHighlightOnEnhancerIfOpenLanded(reelId, 6, false);
            }
            this.playHighlightSymbolsOnReel(data.reelId, false);
        }
    }

    private playHighlightSymbolsOnReel(reelId:number, playSound:boolean = true):boolean {
        let result = false;

        Serial.model.currentSpinPositions[reelId].forEach((spinState, symId) => {
            if([0, 1, 5, 6].includes(symId)) {
                return;
            }
            if(!spinState) {
                return;
            }
            result = this.playHighlightOnSymbol(reelId, symId, playSound) || result;
        });
        return result;
    }

    private playHighlightOnEnhancerIfOpenLanded(reelId:number, symId:number, playSound:boolean = true):boolean {
        const enhancer = Serial.enhancerReels[reelId][symId];
        if(!enhancer.isOpened) {
            return false;
        }
        if(!Serial.model.currentSpinPositions[reelId][symId]) {
            return false;
        }

        return this.playHighlightOnSymbol(reelId, symId, playSound);
    }

    private playHighlightOnSymbol(reelId:number, symbolId:number, playSound:boolean):boolean {
        if(!Serial.model.parsedGameData.winPositions[reelId][symbolId]) {
            return false;
        }

        if(this._positionPlayed[reelId][symbolId]) {
            return false;
        }

        const symbol = Serial.getSymbol(reelId, symbolId);
        let result = false;
        if(this._winSymbols.indexOf(symbol.symbolName) < 0) {
            // To find new win situation.
            Serial.model.parsedGameData.betWinsBeforeReveal.forEach(betWin => {
                if(this._winSymbols.indexOf(betWin.symbol) > -1) {
                    return;
                }
                if(!betWin.reelWinPositions[reelId][symbolId]) {
                    return;
                }

                if(betWin.reelWinPositions[0].some((win, symbolId) => win && this._currentStoppedPositions[0][symbolId]) &&
                    betWin.reelWinPositions[1].some((win, symbolId) => win && this._currentStoppedPositions[1][symbolId]) &&
                    betWin.reelWinPositions[2].some((win, symbolId) => win && this._currentStoppedPositions[2][symbolId])) {
                    this._winSymbols.push(betWin.symbol);
                    result = true;
                }
            });
        }

        if((this._winSymbols.indexOf(symbol.symbolName) > -1 || symbol.symbolName.includes("W"))) {
            this._positionPlayed[reelId][symbolId] = true;
            this.playWinHighlightAnimation(symbol, playSound);
        }

        return result;
    }

    public playWinHighlightAnimation(symbol:SerialSymbol, playSound:boolean):void {
        const effect:WinHighlightEffect = new WinHighlightEffect(symbol.symbolHeight);
        const position:PIXI.Point = symbol.getGlobalPosition();
        effect.position.set(position.x, position.y);
        const tl = gsap.timeline();
        tl.add(() => {
            this._layer.addChild(effect);
            if(playSound && this._highlightSoundPlayedReel < symbol.reelId) {
                this._highlightSoundPlayedReel = symbol.reelId;
                SerialSound.soundPlayer.playTrack({name : Serial.model.parsedGameData.mode.includes("FREESPIN") ? SerialSound.WIN_HIGHLIGHT_FS : SerialSound.WIN_HIGHLIGHT});
            }
        });
        tl.add(effect.playEffect());
        tl.add(() => {
            this._layer.removeChild(effect);
            effect.destroy();
        });
    }

    protected reset() {
        super.reset();
        this._highlightSoundPlayedReel = -1;
        this._currentStoppedPositions = [];
        Serial.model.currentSpinPositions.forEach((reelSpinPositions) => {
            const reelStoppedPositions:boolean[] = [];
            reelSpinPositions.forEach(symSpin => {
                reelStoppedPositions.push(!symSpin);
            });
            this._currentStoppedPositions.push(reelStoppedPositions);
        });

        if(Serial.model.parsedGameData.mode.includes("RESPIN")) {
            if(!this._init) {
                this._init = true;
            } else {
                return;
            }
        }

        this._winSymbols = [];
        this._positionPlayed = ArrayHelper.initArrayWithValues<boolean[]>(SerialGameConfig.REELS_NUM, () => {
            return ArrayHelper.initArrayWithValues<boolean>(7, () => false)
        });
    }
}